package ru.dinarastepina.fabvocab.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import ru.dinarastepina.fabvocab.contracts.toDomain
import ru.dinarastepina.fabvocab.web.api.VocabularyApiClient
import ru.dinarastepina.fabvocab.web.components.FeedbackCard
import ru.dinarastepina.fabvocab.web.components.TopBar
import ru.dinarastepina.fabvocab.web.theme.*

@Page("add")
@Composable
fun AddWordPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val client = remember { VocabularyApiClient() }
    val input = remember { mutableStateOf("") }
    val loading = remember { mutableStateOf(false) }
    val error = remember { mutableStateOf<String?>(null) }
    val result =
        remember { mutableStateOf<ru.dinarastepina.fabvocab.domain.model.WordEntry?>(null) }

    Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
        TopBar(
            title = "Add word",
            navigation = {
                Button(
                    onClick = { ctx.router.navigateTo("/") },
                    colorPalette = SlatePalette,
                    size = ButtonSize.XS,
                    modifier = Modifier.margin(right = 8.px),
                ) {
                    SpanText("← Back")
                }
            },
        )

        TextInput(
            text = input.value,
            onTextChange = { input.value = it },
            modifier = Modifier.fillMaxWidth(),
            placeholder = "Enter an English word...",
            focusBorderColor = Indigo600,
        )

        Row(modifier = Modifier.fillMaxWidth().gap(8.px)) {
            Button(
                onClick = {
                    scope.launch {
                        loading.value = true
                        error.value = null
                        result.value = null
                        try {
                            val word = client.lookupWord(input.value.trim())
                            client.saveWord(word)
                            result.value = word.toDomain()
                            input.value = ""
                        } catch (e: Exception) {
                            error.value = e.message
                        } finally {
                            loading.value = false
                        }
                    }
                },
                enabled = !loading.value && input.value.isNotBlank(),
                colorPalette = IndigoPalette,
                size = ButtonSize.SM,
            ) {
                SpanText(
                    if (loading.value) "Looking up..." else "Lookup & Save",
                    modifier = Modifier.fontWeight(FontWeight.Bold)
                )
            }

            Button(
                onClick = { ctx.router.navigateTo("/") },
                colorPalette = SlatePalette,
                size = ButtonSize.SM,
            ) {
                SpanText("Cancel")
            }
        }

        if (loading.value) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(24.px),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    "Looking up word...",
                    modifier = Modifier.color(Slate500).fontSize(14.px),
                )
            }
        }

        error.value?.let {
            FeedbackCard(message = it, isError = true)
        }

        result.value?.let { word ->
            val theme = posThemeFor(word.partOfSpeech)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(Color("#FFFFFF"))
                    .border(2.px, LineStyle.Solid, Slate100)
                    .borderRadius(24.px)
                    .padding(20.px),
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            word.word,
                            modifier = Modifier
                                .fontSize(22.px)
                                .fontWeight(FontWeight.Black)
                                .color(theme.text),
                        )
                        word.partOfSpeech?.let { pos ->
                            Box(
                                modifier = Modifier
                                    .margin(left = 8.px)
                                    .backgroundColor(theme.background)
                                    .borderRadius(6.px)
                                    .padding(leftRight = 8.px, topBottom = 2.px),
                            ) {
                                SpanText(
                                    pos.lowercase(),
                                    modifier = Modifier
                                        .fontSize(10.px)
                                        .fontWeight(FontWeight.Bold)
                                        .color(theme.text)
                                        .letterSpacing(0.5.px),
                                )
                            }
                        }
                    }
                    word.phonetic?.let {
                        SpanText(
                            it,
                            modifier = Modifier
                                .fontSize(14.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Indigo600),
                        )
                    }
                    if (word.definitions.isNotEmpty()) {
                        SpanText(
                            "Definitions",
                            modifier = Modifier
                                .margin(top = 8.px)
                                .fontSize(12.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Slate400)
                                .letterSpacing(0.5.px),
                        )
                        word.definitions.take(3).forEach { def ->
                            Row(modifier = Modifier.margin(top = 4.px)) {
                                SpanText(
                                    "•",
                                    modifier = Modifier
                                        .margin(right = 8.px)
                                        .fontSize(14.px)
                                        .fontWeight(FontWeight.Bold)
                                        .color(Indigo600),
                                )
                                SpanText(
                                    def,
                                    modifier = Modifier
                                        .fontSize(14.px)
                                        .color(Slate700),
                                )
                            }
                        }
                    }
                    if (word.exampleSentences.isNotEmpty()) {
                        SpanText(
                            "Examples",
                            modifier = Modifier
                                .margin(top = 8.px)
                                .fontSize(12.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Slate400)
                                .letterSpacing(0.5.px),
                        )
                        word.exampleSentences.take(3).forEach { ex ->
                            Row(modifier = Modifier.margin(top = 4.px)) {
                                SpanText(
                                    "\"",
                                    modifier = Modifier
                                        .margin(right = 8.px)
                                        .fontSize(14.px)
                                        .fontWeight(FontWeight.Bold)
                                        .color(Indigo600),
                                )
                                SpanText(
                                    ex,
                                    modifier = Modifier
                                        .fontSize(14.px)
                                        .color(Slate700)
                                        .fontStyle(com.varabyte.kobweb.compose.css.FontStyle.Italic),
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
